import React from 'react';
import {
  Typography,
  Box,
  Paper,
  alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Animation variants reused
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 10
    }
  }
};

const valueVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 120,
      delay: 0.2
    }
  }
};

// Helper duplicated from original source for self-containment
type NumberFormat = 'normal' | 'hundreds' | 'thousands' | 'lakhs';
const formatCurrency = (amount: number, format: NumberFormat = 'normal'): string => {
  const roundedAmount = Math.round(amount);
  let displayAmount = roundedAmount;
  let suffix = '';

  if (format === 'hundreds' && roundedAmount >= 100) {
    displayAmount = Math.round(roundedAmount / 100);
    suffix = 'H';
  } else if (format === 'thousands' && roundedAmount >= 1000) {
    displayAmount = Math.round(roundedAmount / 1000);
    suffix = 'K';
  } else if (format === 'lakhs' && roundedAmount >= 100000) {
    displayAmount = Math.round(roundedAmount / 100000);
    suffix = 'L';
  }

  const formattedValue = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(displayAmount);

  return `${formattedValue}${suffix}`;
};

export interface MetricCardProps {
  title: string;
  value: number;
  percentageChange: number;
  icon: React.ReactNode;
  color: string;
  linkTo: string;
  delay?: number;
  numberFormat?: NumberFormat;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  percentageChange,
  icon,
  color,
  linkTo,
  delay = 0,
  numberFormat = 'normal'
}) => {
  const isPositive = percentageChange >= 0;

  return (
    <motion.div
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      transition={{ delay }}
    >
      <Link
        to={linkTo}
        style={{ textDecoration: 'none' }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            height: '100%',
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(color, 0.15)} 0%, ${alpha(color, 0.05)} 100%)`,
            border: `1px solid ${alpha(color, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            '&:hover': {
              boxShadow: `0 8px 25px ${alpha(color, 0.15)}`,
              transform: 'translateY(-5px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          <Box display="flex" alignItems="center" mb={0.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(color, 0.2),
                color: color,
                mr: 2
              }}
            >
              {icon}
            </Box>
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              {title}
            </Typography>
          </Box>

          <motion.div
            variants={valueVariants}
            initial="hidden"
            animate="visible"
          >
            <Typography variant="h5" fontWeight="bold" sx={{ my: 1, color }}>
              {formatCurrency(value, numberFormat)}
            </Typography>
          </motion.div>

          <Box display="flex" alignItems="flex-start" mt={0.5}>
            {isPositive ? (
              <TrendingUpIcon fontSize="small" sx={{ color: 'success.main', mr: 1, mt: 0.3 }} />
            ) : (
              <TrendingDownIcon fontSize="small" sx={{ color: 'error.main', mr: 1, mt: 0.3 }} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'medium',
                  color: isPositive ? 'success.main' : 'error.main'
                }}
              >
                {isPositive ? '+' : ''}{percentageChange.toFixed(1)}%
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                vs previous month
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Link>
    </motion.div>
  );
};

export interface BalanceCardProps {
  title: string;
  openingValue: number;
  currentValue: number;
  percentageChange: number;
  icon: React.ReactNode;
  color: string;
  linkTo: string;
  delay?: number;
  numberFormat?: NumberFormat;
}

export const BalanceCard: React.FC<BalanceCardProps> = ({
  title,
  openingValue,
  currentValue,
  percentageChange,
  icon,
  color,
  linkTo,
  delay = 0,
  numberFormat = 'normal'
}) => {
  const isPositive = percentageChange >= 0;

  return (
    <motion.div
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      transition={{ delay }}
    >
      <Link
        to={linkTo}
        style={{ textDecoration: 'none' }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            height: '100%',
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
            border: `1px solid ${alpha(color, 0.1)}`,
            cursor: 'pointer',
            '&:hover': {
              boxShadow: `0 5px 15px ${alpha(color, 0.1)}`,
              transform: 'translateY(-3px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          <Box display="flex" alignItems="center" mb={0.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(color, 0.15),
                color: color,
                mr: 1.5
              }}
            >
              {icon}
            </Box>
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              {title}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" mt={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Opening Balance
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="text.primary">
                {formatCurrency(openingValue, numberFormat)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" align="right" display="block">
                Current Balance
              </Typography>
              <Typography variant="body1" fontWeight="bold" color={color}>
                {formatCurrency(currentValue, numberFormat)}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="flex-start" mt={1} justifyContent="flex-end">
            {isPositive ? (
              <TrendingUpIcon fontSize="small" sx={{ color: 'success.main', mr: 0.5, mt: 0.3 }} />
            ) : (
              <TrendingDownIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5, mt: 0.3 }} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 'medium',
                  color: isPositive ? 'success.main' : 'error.main'
                }}
              >
                {isPositive ? '+' : ''}{percentageChange.toFixed(1)}%
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                vs previous
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Link>
    </motion.div>
  );
};
